import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { LangDefinition, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SubscribableComponent } from 'app/modules/shared/subscribable/subscribable.component';
import { UserService } from 'app/modules/shared/user/user.service';
import { MatIconModule } from '@angular/material/icon';
import { User } from 'app/modules/shared/user/user.type';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs';

@Component({
    exportAs: 'languages',
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatIconModule, MatMenuModule, NgTemplateOutlet, TranslocoDirective]
})
export class LanguagesComponent extends SubscribableComponent implements OnInit {
    private readonly _translocoService = inject(TranslocoService);
    private readonly _userService = inject(UserService);
    private _langDefinitions: LangDefinition[];
    private _activeLanguage: string;

    public get activeLanguage(): string { return this._activeLanguage; }
    public get availableLanguages(): LangDefinition[] { return this._langDefinitions; }
    public trackBy(index: number, language: LangDefinition): string { return language.id || index.toString(); }

    public ngOnInit(): void {
        this._langDefinitions = this._translocoService.getAvailableLangs() as LangDefinition[];

        this.subscriptions.push(
            this._userService.user$.pipe(
                distinctUntilChanged((previous, current) => previous.language === current.language),
                filter((user: User) => !!user.language), 
                filter((user: User) => user.language !== this._translocoService.getActiveLang()),
            ).subscribe((user: User) => this._translocoService.setActiveLang(user.language!))
        );

        this.subscriptions.push(
            this._translocoService.langChanges$.pipe(
                startWith(this._translocoService.getActiveLang()), 
                withLatestFrom(this._userService.user$)
            ).subscribe(([activeLanguage, user]) => {
                this._activeLanguage = activeLanguage;
                if (!user.language) {
                    this.updateLanguageForUser(activeLanguage);
                }
            })
        );
    }

    private updateLanguageForUser(language: string): void {
        this.subscriptions.push(
            this._userService.updateLanguage(language)
            .pipe(switchMap(() => this._userService.getMyProfile()))
            .subscribe({ error: (error) => console.error(`Updating language to ${language} failed :`, error) })
        );
    }

    public isActive(languageId: string): boolean { 
        return languageId === this._activeLanguage; 
    }

    public getFlagPath(languageId: string): string {
        return `assets/i18n/flags/${languageId}.svg`;
    }

    public setActiveLanguageAndUpdateForUser(language: string): void {
        this._translocoService.setActiveLang(language);
        this.updateLanguageForUser(language);
        this._activeLanguage = language; 
    }
}
